import React, { useEffect } from 'react';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import './styles.scss';

const CheckIcon = () => (
  <svg
    className="gc-check-icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const sponsoredFeatures = [
  'AI-Guided Compliance Interview and Evidence Compilation',
  'Mathematically Mapped Personal Narrative Package',
  'Instant Audit-Ready PDF Compilation of Your Narrative and Evidence',
  'License fee fully covered by your Prime or State DOT/Agency',
];

const independentFeatures = [
  'Everything in Sponsored Access',
  'Immediate account unlock and processing',
  'Priority 24/7 Support access',
];

const faqs = [
  {
    q: "Can't I just write this myself or use free AI like ChatGPT?",
    a: "You can, but state analysts do not accept generic or emotional essays. A successful submission requires strict legal mapping to 49 CFR Part 26 and hyperlinked financial evidence (tax returns, loan denials). Generic AI tools cannot compile secure, audit-ready Personal Narrative Packages. Furthermore, inputting your highly sensitive corporate financial data into public AI models poses a massive security risk.",
  },
  {
    q: 'Is my financial data secure?',
    a: 'Absolutely. Lusid Certify provides a closed, encrypted, SOC-2 aligned vault. We process your data exclusively to generate your compliant PDF dossier, and your financial information is never used to train public language models.',
  },
  {
    q: 'Will this work for my specific State DOT or UCP?',
    a: 'Yes. The requirement for a subjective Personal Narrative stems from the federal USDOT Interim Final Rule (49 CFR Part 26). Because our architecture maps directly to the federal statute, Because our interview and compilation architecture maps directly to the federal 49 CFR Part 26 statute, the resulting Personal Narrative is structured for submission to any certifying state agency nationwide.',
  },
  {
    q: 'How long does the process take?',
    a: 'Most business owners complete the AI-guided interview and document upload in 45 to 60 minutes. Your fully formatted, mathematically mapped PDF Personal Narrative Package is compiled instantly upon completion, ready to be submitted to your state portal.',
  },
];

const GetCertifiedPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Box className="gc-page">

      {/* ── Header ───────────────────────────────────────────── */}
      <Box className="gc-header">
        <Container maxWidth="lg">
          <Box className="gc-header__inner">
            <Typography className="gc-header__overline">
              49 CFR Compliance Portal
            </Typography>
            <Typography variant="h1" className="gc-header__title">
              Clear your compliance.<br />Zero out-of-pocket costs.
            </Typography>
            <Typography className="gc-header__subtitle">
              Access the Lusid Certify engine for free when sponsored by your Prime Contractor
              or State DOT/Agency. For established businesses needing immediate processing,
              select our independent track.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Positioning Callout ──────────────────────────────── */}
      <Container maxWidth="lg">
        <Box className="gc-positioning">
          <Typography className="gc-positioning__text">
            <strong>Lusid Certify is a compliance structuring tool, not a narrative ghostwriting service.</strong>
            {' '}Our compliance interview draws out the facts of your own experiences, maps them to
            49 CFR evidentiary standards, and compiles your responses into a complete,
            submission-ready Personal Narrative Package with every claim hyperlinked to its proof.
          </Typography>
        </Box>
      </Container>

      {/* ── Pricing Cards ────────────────────────────────────── */}
      <Container maxWidth="lg">
        <Grid container spacing={5} className="gc-pricing-grid" justifyContent="center">

          {/* Sponsored Card */}
          <Grid item xs={12} md={5}>
            <Box className="gc-card gc-card--sponsored">
              <Box className="gc-card__badge">Enterprise Sponsored</Box>
              <Typography className="gc-card__title">Sponsored Access</Typography>
              <Typography className="gc-card__desc">
                For Disadvantaged Business Enterprises (DBEs) partnering with Prime
                Contractors or Government Agencies.
              </Typography>
              <Box className="gc-card__price">
                $0
                <span className="gc-card__price-sub">/ Out of Pocket</span>
              </Box>
              <ul className="gc-card__features">
                {sponsoredFeatures.map((f) => (
                  <li key={f}>
                    <CheckIcon />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Box className="gc-card__actions">
                <Button variant="contained" fullWidth className="gc-btn gc-btn--primary" onClick={() => window.open(process.env.REACT_APP_CERTIFY_URL, '_blank')}>
                  Enter Sponsor Code
                </Button>
                <Button variant="outlined" fullWidth className="gc-btn gc-btn--outline" onClick={() => window.open(process.env.REACT_APP_CERTIFY_URL, '_blank')}>
                  Invite My Prime to Sponsor
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Independent Track Card */}
          <Grid item xs={12} md={5}>
            <Box className="gc-card">
              <Typography className="gc-card__title">Independent Track</Typography>
              <Typography className="gc-card__desc">
                For established businesses seeking immediate, expedited processing without
                waiting for enterprise sponsorship.
              </Typography>
              <Box className="gc-card__price">
                <span className="gc-card__price-original">$499</span>
                $399
                <span className="gc-card__price-sub">/ One-time setup</span>
                <span className="gc-card__price-discount">20% OFF</span>
              </Box>
              <ul className="gc-card__features">
                {independentFeatures.map((f) => (
                  <li key={f}>
                    <CheckIcon />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Box className="gc-card__actions" sx={{ marginTop: 'auto' }}>
                <Button variant="contained" fullWidth className="gc-btn gc-btn--dark" onClick={() => window.open(process.env.REACT_APP_CERTIFY_URL, '_blank')}>
                  Start My Application Now
                </Button>
              </Box>
            </Box>
          </Grid>

        </Grid>
      </Container>

      {/* ── White-Glove Onboarding Callout ──────────────────────────────── */}
      <Box className="gc-onboarding">
        <Container maxWidth="lg">
          <Box className="gc-onboarding__inner">
            <Box className="gc-onboarding__icon">
              <SupportAgentOutlinedIcon />
            </Box>
            <Typography className="gc-onboarding__title">
              Your Complimentary White-Glove Onboarding Call is Included
            </Typography>
            <Typography className="gc-onboarding__body">
              Navigating DBE compliance requires absolute precision. To ensure your application
              perfectly aligns with 49 CFR standards, all new clients receive a complimentary
              15-minute White-Glove Onboarding Call. Your secure booking link will be provided
              immediately after payment. Have questions? Request a callback and our Compliance
              Concierge will reach out directly.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <Box className="gc-faq">
        <Container maxWidth="lg">
          <Box className="gc-faq__header">
            <Typography variant="h2" className="gc-faq__title">
              Frequently Asked Questions
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {faqs.map((faq) => (
              <Grid item xs={12} md={6} key={faq.q}>
                <Box className="gc-faq-box">
                  <Typography className="gc-faq-box__q">{faq.q}</Typography>
                  <Typography className="gc-faq-box__a">{faq.a}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

    </Box>
  );
};

export default GetCertifiedPage;
